import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Upload,
  Camera,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Star,
  Clock,
  Loader2,
  Image as ImageIcon,
  X,
  MessageSquare,
  Calendar,
  Sparkles,
} from 'lucide-react';
import ServiceProviderBadges from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ServiceQuoteModal
 * Quote-First Booking Flow for complex jobs
 * Steps: 1. Describe Work -> 2. Upload Images -> 3. Contact Info -> 4. Submit
 */
const ServiceQuoteModal = ({
  open,
  onClose,
  service,
  onSubmit,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quoteData, setQuoteData] = useState({
    work_type: '',
    work_description: '',
    urgency: 'standard', // 'urgent' | 'standard' | 'flexible'
    budget_range: '',
    images: [],
    measurements: '',
    fabric_preference: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    preferred_contact: 'phone', // 'phone' | 'whatsapp' | 'email'
    additional_notes: '',
  });

  const [imageFiles, setImageFiles] = useState([]);

  if (!service) return null;

  const {
    provider_name,
    provider_image,
    rating = 0,
    review_count = 0,
    verification_level = 0,
    achievements = [],
    quote_services = [],
    response_time,
  } = service;

  const imageUrl = provider_image
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImageFiles([...imageFiles, ...newImages]);
  };

  const removeImage = (index) => {
    const newImages = [...imageFiles];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setImageFiles(newImages);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create FormData for images
      const formData = new FormData();
      imageFiles.forEach((img, index) => {
        formData.append(`image_${index}`, img.file);
      });
      
      await onSubmit?.({
        ...quoteData,
        provider_id: service.id,
        booking_type: 'quote_request',
        image_count: imageFiles.length,
      }, formData);
      
      setStep(5); // Success step
    } catch (error) {
      console.error('Quote request failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return quoteData.work_type && quoteData.work_description;
      case 2:
        return true; // Images are optional
      case 3:
        return quoteData.customer_name && quoteData.customer_phone;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const URGENCY_OPTIONS = [
    { id: 'urgent', label: 'Urgent', sublabel: 'Within 1 week', color: 'text-red-600 bg-red-50' },
    { id: 'standard', label: 'Standard', sublabel: '2-4 weeks', color: 'text-blue-600 bg-blue-50' },
    { id: 'flexible', label: 'Flexible', sublabel: 'No rush', color: 'text-green-600 bg-green-50' },
  ];

  const BUDGET_OPTIONS = [
    'Under Rs. 5,000',
    'Rs. 5,000 - 10,000',
    'Rs. 10,000 - 25,000',
    'Rs. 25,000 - 50,000',
    'Above Rs. 50,000',
    'Need Quote First',
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Provider Header */}
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <img
              src={imageUrl}
              alt={provider_name}
              className="w-12 h-12 rounded-full object-cover border-2 border-purple-200"
            />
            <div className="flex-1">
              <DialogTitle className="text-lg">{provider_name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-medium">{rating.toFixed(1)}</span>
                </div>
                <ServiceProviderBadges
                  verificationLevel={verification_level}
                  achievements={achievements}
                  maxBadges={2}
                />
              </div>
            </div>
            {response_time && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {response_time}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Progress Steps */}
        {step <= 4 && (
          <div className="flex items-center justify-between px-2 py-4">
            {['Describe', 'Images', 'Contact', 'Review'].map((label, index) => (
              <div key={label} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > index + 1
                      ? 'bg-green-500 text-white'
                      : step === index + 1
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > index + 1 ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`w-12 h-1 mx-1 ${
                      step > index + 1 ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Describe Work */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Tell us about your requirements
            </DialogDescription>
            
            {/* Work Type Selection */}
            <div>
              <Label>What type of work do you need? *</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {quote_services.map((svc) => (
                  <div
                    key={svc}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                      quoteData.work_type === svc
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setQuoteData({ ...quoteData, work_type: svc })}
                    data-testid={`work-type-${svc}`}
                  >
                    <span className="text-sm font-medium">{svc}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="work_description">Describe your requirements *</Label>
              <Textarea
                id="work_description"
                value={quoteData.work_description}
                onChange={(e) =>
                  setQuoteData({ ...quoteData, work_description: e.target.value })
                }
                placeholder="Please describe what you need in detail..."
                rows={4}
                data-testid="quote-description"
              />
            </div>
            
            {/* Urgency */}
            <div>
              <Label>Timeline</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {URGENCY_OPTIONS.map((opt) => (
                  <div
                    key={opt.id}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                      quoteData.urgency === opt.id
                        ? `border-purple-500 ${opt.color}`
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setQuoteData({ ...quoteData, urgency: opt.id })}
                  >
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.sublabel}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Budget Range */}
            <div>
              <Label>Budget Range</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {BUDGET_OPTIONS.map((budget) => (
                  <div
                    key={budget}
                    className={`p-2 border-2 rounded-lg cursor-pointer transition-all text-center text-sm ${
                      quoteData.budget_range === budget
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    onClick={() => setQuoteData({ ...quoteData, budget_range: budget })}
                  >
                    {budget}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Upload Images */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Add reference images (optional but recommended)
            </DialogDescription>
            
            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                id="image-upload"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                  <Camera className="h-6 w-6 text-purple-600" />
                </div>
                <p className="font-medium text-gray-900">Upload Images</p>
                <p className="text-sm text-gray-500 mt-1">
                  Add photos of fabric, design inspiration, or garment to alter
                </p>
              </label>
            </div>
            
            {/* Image Previews */}
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {imageFiles.map((img, index) => (
                  <div key={index} className="relative">
                    <img
                      src={img.preview}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Optional Details */}
            <div className="space-y-4 pt-4 border-t">
              <div>
                <Label htmlFor="measurements">Measurements (if known)</Label>
                <Textarea
                  id="measurements"
                  value={quoteData.measurements}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, measurements: e.target.value })
                  }
                  placeholder="E.g., Bust: 36&quot;, Waist: 28&quot;, Length: 42&quot;"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="fabric">Fabric Preference (if any)</Label>
                <Input
                  id="fabric"
                  value={quoteData.fabric_preference}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, fabric_preference: e.target.value })
                  }
                  placeholder="E.g., Silk, Cotton, Chiffon"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              How should the provider contact you?
            </DialogDescription>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Full Name *</Label>
                <Input
                  id="customer_name"
                  value={quoteData.customer_name}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, customer_name: e.target.value })
                  }
                  placeholder="Enter your name"
                  data-testid="quote-customer-name"
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Phone Number *</Label>
                <Input
                  id="customer_phone"
                  value={quoteData.customer_phone}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, customer_phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                  data-testid="quote-customer-phone"
                />
              </div>
              <div>
                <Label htmlFor="customer_email">Email (Optional)</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={quoteData.customer_email}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, customer_email: e.target.value })
                  }
                  placeholder="Enter email"
                />
              </div>
              
              {/* Preferred Contact Method */}
              <div>
                <Label>Preferred Contact Method</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { id: 'phone', label: 'Phone Call', icon: '📞' },
                    { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
                    { id: 'email', label: 'Email', icon: '✉️' },
                  ].map((method) => (
                    <div
                      key={method.id}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                        quoteData.preferred_contact === method.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() =>
                        setQuoteData({ ...quoteData, preferred_contact: method.id })
                      }
                    >
                      <span className="text-xl">{method.icon}</span>
                      <p className="text-xs font-medium mt-1">{method.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="additional_notes">Additional Notes</Label>
                <Textarea
                  id="additional_notes"
                  value={quoteData.additional_notes}
                  onChange={(e) =>
                    setQuoteData({ ...quoteData, additional_notes: e.target.value })
                  }
                  placeholder="Any other details..."
                  rows={2}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Review your quote request
            </DialogDescription>
            
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-gray-600">Work Type</span>
                <span className="font-medium">{quoteData.work_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Description</span>
                <p className="text-sm mt-1">{quoteData.work_description}</p>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Timeline</span>
                <Badge variant="outline">
                  {URGENCY_OPTIONS.find((o) => o.id === quoteData.urgency)?.label}
                </Badge>
              </div>
              {quoteData.budget_range && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Budget</span>
                  <span className="font-medium">{quoteData.budget_range}</span>
                </div>
              )}
              {imageFiles.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Images</span>
                  <span className="flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" />
                    {imageFiles.length} attached
                  </span>
                </div>
              )}
              <div className="pt-3 border-t">
                <div className="flex justify-between">
                  <span className="text-gray-600">Contact</span>
                  <span className="font-medium">{quoteData.customer_name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Phone</span>
                  <span>{quoteData.customer_phone}</span>
                </div>
              </div>
            </div>
            
            {/* What happens next */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                What happens next?
              </h4>
              <ul className="mt-2 space-y-2 text-sm text-purple-800">
                <li className="flex items-start gap-2">
                  <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Provider will review your request and respond within {response_time || '24 hours'}
                </li>
                <li className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  You&apos;ll receive a quote with pricing and timeline
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  Accept the quote to confirm your booking
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Sent!</h3>
            <p className="text-gray-600 mb-4">
              {provider_name} will review your request and get back to you soon.
            </p>
            <div className="p-4 bg-gray-50 rounded-lg text-left text-sm">
              <p><strong>Work Type:</strong> {quoteData.work_type}</p>
              <p><strong>Timeline:</strong> {URGENCY_OPTIONS.find((o) => o.id === quoteData.urgency)?.label}</p>
              <p><strong>Expected Response:</strong> Within {response_time || '24 hours'}</p>
            </div>
            <Button
              className="mt-6 bg-purple-600 hover:bg-purple-700"
              onClick={onClose}
              data-testid="quote-success-close"
            >
              Done
            </Button>
          </div>
        )}

        {/* Footer Actions */}
        {step <= 4 && (
          <DialogFooter className="flex justify-between pt-4 border-t">
            {step > 1 ? (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            {step < 4 ? (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleNext}
                disabled={!canProceed()}
                data-testid="quote-next-step"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                data-testid="quote-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Quote Request
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceQuoteModal;
